const DETAIL_KEYS = new Set([
  'price',
  'amount',
  'description',
  'name',
  'label',
  'title',
  'id',
]);

const money = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.round(num * 100) / 100;
};

const isAddonDetail = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return keys.length > 0 && keys.every((key) => DETAIL_KEYS.has(key));
};

const emptyAddon = () => ({ name: '', description: '', price: '' });

const toAddon = (name, detail) => {
  if (detail == null || detail === '') {
    const label = String(name || '').trim();
    return label ? { name: label, description: '', price: 0 } : null;
  }
  if (typeof detail === 'string' || typeof detail === 'number') {
    const label = String(name || detail || '').trim();
    const price = typeof detail === 'number' ? money(detail) : 0;
    const description = typeof detail === 'string' && name ? String(detail).trim() : '';
    if (!label) return null;
    return {
      name: label,
      description: description && description !== label ? description : '',
      price,
    };
  }
  if (typeof detail !== 'object') return null;
  const title = String(detail.name || detail.label || detail.title || name || '').trim();
  if (!title) return null;
  const desc = String(detail.description || '').trim();
  return {
    name: title,
    description: desc && desc !== title ? desc : '',
    price: money(detail.price ?? detail.amount ?? 0),
  };
};

/**
 * Stored package add-ons as { name, description, price }.
 * Accepts string lists, name→price maps, and already structured rows.
 */
export const normalizeAddonOptions = (value) => {
  if (value == null || value === '') return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      return normalizeAddonOptions(JSON.parse(trimmed));
    } catch {
      return [{ name: trimmed, description: '', price: 0 }];
    }
  }
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          const row = toAddon('', item);
          return row ? [row] : [];
        }
        return normalizeAddonOptions(item);
      })
      .filter((row) => row?.name);
  }
  if (typeof value === 'object') {
    if (isAddonDetail(value)) {
      const row = toAddon('', value);
      return row ? [row] : [];
    }
    return Object.entries(value)
      .map(([name, detail]) => toAddon(name, detail))
      .filter(Boolean);
  }
  return [];
};

export const addonOptionLabel = (addon) => {
  const row = normalizeAddonOptions(addon)[0];
  if (!row) return '';
  const price =
    Number(row.price) > 0 ? ` (+$${Number(row.price).toFixed(2).replace(/\.00$/, '')})` : '';
  return `${row.name}${price}${row.description ? ` — ${row.description}` : ''}`;
};

export const addonOptionsToStrings = (value) =>
  normalizeAddonOptions(value).map((row) => addonOptionLabel(row)).filter(Boolean);

export const blankAddonOption = emptyAddon;

export const addonOptionsTotal = (addons = []) =>
  normalizeAddonOptions(addons).reduce((sum, row) => sum + money(row.price), 0);
